import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useTranslation, useDataTracking} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {FreelancerVerticalCompatibilityWithStrategyInner, FreelancerVerticalStateInner, ReasonForRefusal} from '@api';
import {Configuration, FreelancerVertical, FreelancerVerticalApplicationStatus, FreelancerVerticalStateApi} from '@api';

const apiConfiguration = new Configuration({basePath: ''});
const freelancerVerticalStateApi = new FreelancerVerticalStateApi(apiConfiguration);

export const useFreelancerVerticalStore = defineStore('freelancerVertical', () => {
    const {t} = useTranslation();
    const {track} = useDataTracking();

    const initialized = computed(() => !!freelancerProfileId.value);
    const loading = ref(false);
    const verticalChangesEnabled = ref(true);

    const freelancerProfileId = ref('');
    const freelancerVerticalState = ref<FreelancerVerticalStateInner[]>([]);
    const compatibilityToStrategyPromotion = ref<FreelancerVerticalCompatibilityWithStrategyInner[]>([]);
    const freelancerFirstName = ref('');
    const freelancerLastName = ref('');
    const freelancerEmailAddress = ref('');

    async function init(profileId: string, enableVerticalChanges: boolean, emailAddress?: string, firstName?: string, lastName?: string) {
        if (initialized.value && freelancerProfileId.value === profileId) {
            return;
        }

        loading.value = true;
        try {
            const freelancerVerticalStateResource = await freelancerVerticalStateApi.getById({profileId});
            freelancerVerticalState.value = freelancerVerticalStateResource.state;
            compatibilityToStrategyPromotion.value = freelancerVerticalStateResource.compatibilityWithStrategy;
            freelancerProfileId.value = profileId;
            verticalChangesEnabled.value = enableVerticalChanges;
            freelancerEmailAddress.value = emailAddress ?? '';
            freelancerFirstName.value = firstName ?? '';
            freelancerLastName.value = lastName ?? '';
        } finally {
            loading.value = false;
        }

        return {
            freelancerVerticalState: freelancerVerticalState.value,
        };
    }

    function checkInitialized() {
        if (!initialized.value) {
            throw new Error('The store is not initialized yet');
        }
    }

    function checkVerticalChangesEnabled() {
        if (!verticalChangesEnabled.value) {
            throw new Error('Vertical changes are disabled');
        }
    }

    const isRegularAccepted = computed<boolean>(() => isVerticalInStatus(FreelancerVertical.Regular, FreelancerVerticalApplicationStatus.Accepted));
    const isStrategyAccepted = computed<boolean>(() => isVerticalInStatus(FreelancerVertical.Strategy, FreelancerVerticalApplicationStatus.Accepted));
    const isStrategyPending = computed<boolean>(() => isVerticalInStatus(FreelancerVertical.Strategy, FreelancerVerticalApplicationStatus.Pending));
    const isStrategyPendingOrAccepted = computed<boolean>(() => isStrategyPending.value || isStrategyAccepted.value);

    function isVerticalInStatus(vertical: FreelancerVertical, status: FreelancerVerticalApplicationStatus) {
        return freelancerVerticalState.value.some(
            (value) => value.freelancerVertical === vertical && value.freelancerVerticalApplicationStatus === status,
        );
    }

    async function suggestToStrategy(reason: string) {
        checkInitialized();
        checkVerticalChangesEnabled();

        loading.value = true;
        try {
            freelancerVerticalState.value = (
                await freelancerVerticalStateApi.suggestToStrategy({
                    profileId: freelancerProfileId.value,
                    reasonForRequest: {reason},
                })
            ).state;
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.suggest.to.strategy.notification.success'),
                    level: 'success',
                },
            });
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.suggest.to.strategy.notification.error'),
                    level: 'error',
                },
            });
        } finally {
            loading.value = false;
        }
    }

    const acceptedVertical = computed<string | undefined>(
        () => freelancerVerticalState.value.find((item) => item.freelancerVerticalApplicationStatus === 'ACCEPTED')?.freelancerVertical,
    );

    async function acceptToStrategy() {
        checkInitialized();
        checkVerticalChangesEnabled();

        loading.value = true;
        const previousVertical = acceptedVertical.value;

        try {
            freelancerVerticalState.value = (
                await freelancerVerticalStateApi.acceptToStrategy({
                    profileId: freelancerProfileId.value,
                })
            ).state;
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.accept.notification.success', [
                        freelancerFirstName.value,
                        freelancerLastName.value,
                        freelancerEmailAddress.value,
                    ]),
                    level: 'success',
                },
            });

            track('vetting_completed', {
                profile_id: freelancerProfileId.value,
                previous_vertical: previousVertical,
                vetting_answer: 'yes',
                rejection_reason: null,
            });
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.accept.notification.error'),
                    level: 'error',
                },
            });
        } finally {
            loading.value = false;
        }
    }

    async function refuseToStrategy(reason: ReasonForRefusal, locale: string, emailSubject: string, emailBody: string) {
        checkInitialized();
        checkVerticalChangesEnabled();

        loading.value = true;
        const previousVertical = acceptedVertical.value;

        try {
            freelancerVerticalState.value = (
                await freelancerVerticalStateApi.refuseToStrategy({
                    profileId: freelancerProfileId.value,
                    strategyRefusalRequest: {reason, locale, emailSubject, emailBody},
                })
            ).state;
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.redirect.notification.success'),
                    level: 'success',
                },
            });
            track('vetting_completed', {
                profile_id: freelancerProfileId.value,
                previous_vertical: previousVertical,
                vetting_answer: 'no',
                rejection_reason: reason,
            });
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.redirect.notification.error'),
                    level: 'error',
                },
            });
        } finally {
            loading.value = false;
        }
    }

    async function refuseSilentlyToStrategy() {
        checkInitialized();
        checkVerticalChangesEnabled();

        loading.value = true;
        try {
            freelancerVerticalState.value = (
                await freelancerVerticalStateApi.refuseSilentlyToStrategy({
                    profileId: freelancerProfileId.value,
                })
            ).state;
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.cancel.suggestion.notification.success'),
                    level: 'success',
                },
            });
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.cancel.suggestion.notification.error'),
                    level: 'error',
                },
            });
        } finally {
            loading.value = false;
        }
    }

    async function acceptToRegular(reason: string) {
        checkInitialized();
        checkVerticalChangesEnabled();

        loading.value = true;
        try {
            freelancerVerticalState.value = (
                await freelancerVerticalStateApi.acceptToRegular({
                    profileId: freelancerProfileId.value,
                    reasonForRequest: {reason},
                })
            ).state;
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.redirect.notification.success'),
                    level: 'success',
                },
            });
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.vetting.community.vetting.decision.redirect.notification.error'),
                    level: 'error',
                },
            });
        } finally {
            loading.value = false;
        }
    }

    return {
        init,
        verticalChangesEnabled: readonlyState(verticalChangesEnabled),
        loading: readonlyState(loading),
        freelancerVerticalState: readonlyState(freelancerVerticalState),
        compatibilityToStrategyPromotion: readonlyState(compatibilityToStrategyPromotion),
        isStrategyPending: readonlyState(isStrategyPending),
        isRegularAccepted: readonlyState(isRegularAccepted),
        isStrategyAccepted: readonlyState(isStrategyAccepted),
        isStrategyPendingOrAccepted: readonlyState(isStrategyPendingOrAccepted),
        suggestToStrategy,
        acceptToStrategy,
        refuseToStrategy,
        refuseSilentlyToStrategy,
        acceptToRegular,
    };
});
